/**
 * Write-through queue with retry (#655)
 *
 * Wraps Supabase fire-and-forget writes with a localStorage-backed
 * retry queue. Failed writes are stored and retried on next flush().
 *
 * Usage:
 *   writeThroughQueue.enqueue(() => sb.from('x').upsert({...}), 'reaction-upsert');
 *   writeThroughQueue.flush(); // on app startup or periodic
 */
import { createLogger } from './logger';

const log = createLogger('WriteQueue');
const STORAGE_KEY = 'ig-write-queue';
const MAX_RETRIES = 3;
const MAX_QUEUE_SIZE = 50;

interface QueueEntry {
  id: string;
  table: string;
  op: 'upsert' | 'insert' | 'delete';
  payload: Record<string, unknown>;
  match?: Record<string, unknown>;
  retries: number;
  created_at: string;
}

function loadQueue(): QueueEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore corrupt data */ }
  return [];
}

function saveQueue(queue: QueueEntry[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(queue.slice(-MAX_QUEUE_SIZE)));
  } catch { /* ignore */ }
}

/**
 * Execute a Supabase write-through operation with retry on failure.
 * If the write fails, it's queued in localStorage for retry.
 * #686: Returns false on failure so callers can optionally rollback.
 */
export async function writeThrough(
  getSupabase: () => ReturnType<typeof import('@/lib/supabase').getSupabase>,
  table: string,
  op: 'upsert' | 'insert' | 'delete',
  payload: Record<string, unknown>,
  match?: Record<string, unknown>,
  label?: string,
): Promise<boolean> {
  try {
    const sb = getSupabase();
    let query;
    if (op === 'delete') {
      query = sb.from(table).delete();
      for (const [k, v] of Object.entries(match ?? payload)) {
        query = query.eq(k, v as string);
      }
    } else if (op === 'upsert') {
      query = sb.from(table).upsert(payload);
    } else {
      query = sb.from(table).insert(payload);
    }
    const { error } = await query;
    if (error) {
      log.warn(`Write-through ${op} failed, queuing for retry`, { table, label, err: error.message });
      enqueueRetry(table, op, payload, match);
      _notifyFailure(table, op, label);
      return false;
    }
    return true;
  } catch (err) {
    log.warn(`Write-through ${op} threw, queuing for retry`, { table, label, err: String(err) });
    enqueueRetry(table, op, payload, match);
    _notifyFailure(table, op, label);
    return false;
  }
}

/**
 * #686: Global write-failure listener — stores can register a callback
 * to show a toast or rollback optimistic state on failure.
 */
type WriteFailureListener = (table: string, op: string, label?: string) => void;
let _failureListener: WriteFailureListener | null = null;

export function onWriteFailure(listener: WriteFailureListener | null): void {
  _failureListener = listener;
}

function _notifyFailure(table: string, op: string, label?: string) {
  if (_failureListener) {
    try { _failureListener(table, op, label); } catch { /* no-op */ }
  }
}

function enqueueRetry(
  table: string,
  op: 'upsert' | 'insert' | 'delete',
  payload: Record<string, unknown>,
  match?: Record<string, unknown>,
) {
  const queue = loadQueue();
  queue.push({
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    table, op, payload, match,
    retries: 0,
    created_at: new Date().toISOString(),
  });
  saveQueue(queue);
}

/**
 * Flush the retry queue — call on app startup or periodically.
 * Returns count of successfully retried writes.
 */
export async function flushWriteQueue(
  getSupabase: () => ReturnType<typeof import('@/lib/supabase').getSupabase>,
): Promise<number> {
  const queue = loadQueue();
  if (queue.length === 0) return 0;

  log.info('Flushing write queue', { count: queue.length });
  const remaining: QueueEntry[] = [];
  let success = 0;

  for (const entry of queue) {
    if (entry.retries >= MAX_RETRIES) {
      log.warn('Write exceeded max retries, dropping', { table: entry.table, id: entry.id });
      continue; // drop it
    }
    try {
      const sb = getSupabase();
      let query;
      if (entry.op === 'delete') {
        query = sb.from(entry.table).delete();
        for (const [k, v] of Object.entries(entry.match ?? entry.payload)) {
          query = query.eq(k, v as string);
        }
      } else if (entry.op === 'upsert') {
        query = sb.from(entry.table).upsert(entry.payload);
      } else {
        query = sb.from(entry.table).insert(entry.payload);
      }
      const { error } = await query;
      if (error) {
        entry.retries++;
        remaining.push(entry);
      } else {
        success++;
      }
    } catch {
      entry.retries++;
      remaining.push(entry);
    }
  }

  saveQueue(remaining);
  if (success > 0) log.info('Write queue flushed', { success, remaining: remaining.length });
  return success;
}